import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import StyledCollapsibleListItem from './CollpasibleListItem.style';
import { Meta, Body } from '../../styled components/Typography';
import {
  TitleHeaderContainer,
  TitleHeaderContent,
  TitleHeaderContentLabel
} from '../TitleHeader/TitleHeader.style';
import Sticky from '../../styled components/Sticky';
import Button from '../Button/Button';

/**
 * Use inside the <a href="/docs/components-list--standard">List</a> component.
 *
 * A `CollapsibleListItem` has a label and keeps track of its own collapsed state.
 *
 * The initial collapsed state can be set via the `initiallyCollapsed` prop, which defaults to collapsed.
 *
 * The label layout (size and color) can be customised via the labelOptions prop, while the icon's layout (size, color) can be customised via the iconOptions.
 *
 * The onClick handler can be used to execute additional logic when the collapsed state is changed. It receives the new collapsed state as a argument.
 *
 * For testing purposes, an object of `data-* attributes` can be passed, which are added on the HTML element surrounding the label.
 */

const CollapsibleListItem = ({
  initiallyCollapsed = true,
  label,
  children,
  labelOptions = {
    size: 'default',
    color: 'default'
  },
  dataAttributes = {},
  iconOptions = {
    size: 'medium'
  },
  onClick = () => {},
  className = '',
  variant = 'default',
  stickyTop = 0,
  stickyHeader = false,
  rightElement = null,
  stickyHeaderRef = {}
}) => {
  const [collapsed, setCollapsed] = useState(initiallyCollapsed);
  const labelColor = labelOptions.color === 'gray' ? 'gray dark' : 'default';

  function toggleCollapsed() {
    const currentCollapseStatus = collapsed;
    setCollapsed(!currentCollapseStatus);
    onClick(!currentCollapseStatus);
  }

  function renderTextCollapibleListItem() {
    return (
      <StyledCollapsibleListItem
        icon={collapsed ? 'expand-less' : 'expand-more'}
        iconOptions={iconOptions}
        onClick={toggleCollapsed}
        className={className}
        useIconAsButton
      >
        <p tw="flex h-24 items-center" {...dataAttributes}>
          {labelOptions.size === 'small' ? (
            <Meta $color={labelColor}>{label}</Meta>
          ) : (
            <Body $color={labelColor}>{label}</Body>
          )}
        </p>
        {rightElement}
      </StyledCollapsibleListItem>
    );
  }

  function renderStyleVariantCollapsibleListItem() {
    return (
      <StyledCollapsibleListItem
        onClick={toggleCollapsed}
        className={className}
        $variant={variant}
        icon={false}
        useIconAsButton
      >
        <TitleHeaderContainer
          data-test-type="titleheader"
          $variant={variant}
          tw="flex-grow"
        >
          <TitleHeaderContent
            className={className}
            tw="flex"
            $variant={variant}
            {...dataAttributes}
          >
            {label && (
              <TitleHeaderContentLabel
                data-test-type="title"
                $transform="uppercase"
                tw="flex break-words min-w-0 py-2"
                $variant={variant}
              >
                <Button
                  tw="p-0 mr-4 ml-4 my-2 text-black opacity-60 flex flex-shrink-0"
                  contentType="icon"
                  size="small"
                  icon={collapsed ? 'expand-less' : 'expand-more'}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCollapsed();
                  }}
                />

                {label}
              </TitleHeaderContentLabel>
            )}
            {rightElement}
          </TitleHeaderContent>
        </TitleHeaderContainer>
      </StyledCollapsibleListItem>
    );
  }

  useEffect(() => {
    if (initiallyCollapsed !== collapsed) {
      setCollapsed(initiallyCollapsed);
    }
  }, [initiallyCollapsed]);

  const ConditionalWrapper = ({ condition, wrapper, children }) =>
    condition ? wrapper(children) : children;

  return (
    <>
      <ConditionalWrapper
        condition={stickyHeader}
        wrapper={(children) => (
          <Sticky
            $zIndex={4}
            $top={stickyTop}
            tw="flex-grow"
            data-js-sticky-header
            ref={stickyHeaderRef}
          >
            {children}
          </Sticky>
        )}
      >
        {variant === 'default'
          ? renderTextCollapibleListItem()
          : renderStyleVariantCollapsibleListItem()}
      </ConditionalWrapper>
      {!collapsed && children}
    </>
  );
};

CollapsibleListItem.propTypes = {
  initiallyCollapsed: PropTypes.bool,
  children: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  labelOptions: PropTypes.shape({
    size: PropTypes.oneOf(['small', 'default']),
    color: PropTypes.oneOf(['default', 'gray'])
  }),
  iconOptions: PropTypes.shape({
    size: PropTypes.string
  }),
  dataAttributes: PropTypes.shape({}),
  /** Additional onClick event, toggling collapsed state is already handled inside component */
  onClick: PropTypes.func,
  /** ClassName is supported such that styled components can pass extra styling.
   * Do not use this to adjust the styling, because the ordering of the applied tailwind classes is undeterministic */
  className: PropTypes.string,
  /** Primary variant is displayed in full width with background color & icon inside  */
  variant: PropTypes.oneOf(['default', 'primary', 'secondary']),
  /** Should the header inside the collapsible listitem be sticky? */
  stickyHeader: PropTypes.bool,
  stickyTop: PropTypes.number,
  rightElement: PropTypes.node,
  stickyHeaderRef: PropTypes.shape({})
};

export default CollapsibleListItem;
